import _ from 'lodash';
import i18n from 'i18next';

Mobi2Go._class = Mobi2Go._class || {};

const { validator } = Mobi2Go.helpers;

/**-----------------------------------------------------------------------------
 */
var Address = (Mobi2Go._class.CustomerAddress = function(
    properties,
    customer,
    callback
) {
    if (!(this instanceof Address)) {
        return new Address(properties, customer, callback);
    }
    this.init(properties, customer, callback);
});

Address.getSettings = function() {
    const country = Mobi2Go.headoffice.country_name;
    return _.merge(
        {},
        ADDRESS_SETTINGS.default,
        ADDRESS_SETTINGS[country] || {},
        ADDRESS_SETTINGS.custom
    );
};

Address.overrideSettings = function(settings) {
    ADDRESS_SETTINGS.custom = settings;
};

Address.PROPERTIES = {
    TYPE: 'type',
    NAME: 'name',
    SPECIFIC_NUMBER: 'specific_number',
    STREET_NUMBER: 'street_number',
    STREET_NAME: 'street_name',
    POST_CODE: 'post_code',
    SUBURB: 'suburb',
    CITY: 'city',
    REGION: 'region',
    LATITUDE: 'latitude',
    LONGITUDE: 'longitude',
    COMMENT: 'comment',
};

_.extend(Address.prototype, {
    /**-------------------------------------------------------------------------
     */
    init(properties, customer, callback) {
        this.customer = customer;
        Mobi2Go.app.mixinEvents(_.uniqueId('CustomerAddress'), this);

        this.locale_settings = Address.getSettings();
        this.set(properties, callback);
    },

    /**-------------------------------------------------------------------------
     */
    set(properties, save_callback) {
        this.id = (properties && +properties.id) || this.id || null;

        this.type =
            (properties && properties.type) ||
            this.getPropertyDefaultValue('type');
        this.name =
            (properties && properties.name) ||
            this.getPropertyDefaultValue('name');
        this.specific_number =
            (properties && properties.specific_number) ||
            this.getPropertyDefaultValue('specific_number');
        this.street_number =
            (properties && properties.street_number) ||
            this.getPropertyDefaultValue('street_number');
        this.street_name =
            (properties && properties.street_name) ||
            this.getPropertyDefaultValue('street_name');
        this.post_code =
            (properties && properties.post_code) ||
            this.getPropertyDefaultValue('post_code');
        this.comment =
            (properties && properties.comment) ||
            this.getPropertyDefaultValue('comment');
        this.geocode_search_string =
            (properties && properties.geocode_search_string) || '';

        this.suburb =
            (properties && properties.suburb) ||
            this.getPropertyDefaultValue('suburb');
        this.suburb_id = (properties && properties.suburb_id) || null;
        this.city =
            (properties && properties.city) ||
            this.getPropertyDefaultValue('city');
        this.city_id = (properties && properties.city_id) || null;
        this.region =
            (properties && properties.region) ||
            this.getPropertyDefaultValue('region');
        this.region_id = (properties && properties.region_id) || null;

        this.latitude =
            (properties && +properties.latitude) ||
            this.getPropertyDefaultValue('latitude');
        this.longitude =
            (properties && +properties.longitude) ||
            this.getPropertyDefaultValue('longitude');

        this.exclusive_order_id =
            (properties && properties.exclusive_order_id) || null;

        if (Mobi2Go.Customer.getPhoneNumber()) {
            this.phone_number = Mobi2Go.Customer.getPhoneNumber();
        }
        if (Mobi2Go.Customer.isGuest() && this.exclusive_order_id === null) {
            this.exclusive_order_id = Mobi2Go.Order.getId();
        }

        if (this.isValid() && save_callback) {
            this.save(save_callback);
        }

        this.trigger('update');

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    getSettings: Address.getSettings,

    /**-------------------------------------------------------------------------
     */
    update(properties) {
        this.set(_.extend(this.get(), properties));

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    isValid() {
        try {
            this.validate();

            return true;
        } catch (error) {
            return false;
        }
    },

    isPropertyEditable(property) {
        const { editable } = Address.getSettings().properties[property];

        return _.isFunction(editable) ? editable(this) : !!editable;
    },
    isPropertyVisible(property) {
        const { visible } = Address.getSettings().properties[property];

        return _.isFunction(visible) ? visible(this) : !!visible;
    },
    getPropertyDefaultValue(property) {
        const { default_value } = Address.getSettings().properties[property];

        return _.isFunction(default_value)
            ? default_value(this)
            : default_value || '';
    },
    getPropertyLabel(property) {
        const { label } = Address.getSettings().properties[property];

        return _.isFunction(label) ? label(this) : label || '';
    },
    getPropertyPlaceholder(property) {
        const { placeholder } = Address.getSettings().properties[property];

        return _.isFunction(placeholder)
            ? placeholder(this)
            : placeholder || '';
    },
    isPropertyRequired(property) {
        const { required } = Address.getSettings().properties[property];

        return _.isFunction(required) ? required(this) : !!required;
    },

    /**-------------------------------------------------------------------------
     */
    validate(address) {
        address = address || this;

        _.each(Address.PROPERTIES, function(PROPERTY) {
            const { validate } = Address.getSettings().properties[PROPERTY];

            if (validate) {
                const validation = validate(address);
                if (!validation.is_valid) {
                    throw new Error(validation.message);
                }
            }
        });
    },

    /**-------------------------------------------------------------------------
     */
    isSaved() {
        return !!this.id;
    },

    /**-------------------------------------------------------------------------
     */
    get(options) {
        return _.pick(this, [
            'id',
            'type',
            'name',
            'specific_number',
            'street_number',
            'street_name',
            'suburb',
            'city',
            'region',
            'suburb_id',
            'city_id',
            'region_id',
            'phone_number',
            'post_code',
            'comment',
            'geocode_search_string',
            'latitude',
            'longitude',
            'exclusive_order_id',
        ]);
    },

    /**-------------------------------------------------------------------------
     */
    save(callback) {
        const that = this;
        let method = 'POST';
        const endpoint = ['customers', this.customer.email, 'addresses'];

        // Save existing address
        if (this.id && this.id !== 'new') {
            endpoint.push(this.id);
            method = 'PUT';
        }

        this.validate();

        const payload = this.get();
        if (payload.city === '') {
            delete payload.city;
        }

        if (payload.city_id === null) {
            delete payload.city_id;
        }

        Mobi2Go.app.serverRequest({
            api_version: 1,
            endpoint,
            type: method,
            data: payload,
            success(data) {
                if (data.id !== undefined && data.id) {
                    that.set(data);
                }
                that.trigger('saved');
                if (_.isFunction(callback)) {
                    callback(that);
                }
            },
        });

        return this;
    },

    /**-------------------------------------------------------------------------
     */
    getPredictionQueryString() {
        let line = '';

        if (this.specific_number && !this.street_number && !this.name) {
            line += `${this.specific_number} `;
        }

        if (this.name && this.type !== 'residential') {
            line += `${this.name} `;
        }

        if (this.street_number) {
            line += `${this.street_number} `;
        }

        if (this.street_name) {
            line += `${this.street_name} `;
        }

        if (this.suburb) {
            line += `${this.suburb} `;
        }

        return line;
    },

    /**-------------------------------------------------------------------------
     */
    mapGmapsPlaceDetailFields(details, prediction_description) {
        const properties = {};
        if (!details) {
            return properties;
        }
        if (details.geometry) {
            properties.latitude = details.geometry.location.lat();
            properties.longitude = details.geometry.location.lng();
        }

        const map = Address.getSettings().gmap_address_mapping;

        if (_.intersection(details.types, ['lodging']).length) {
            properties.type = 'hotel';
            if (details.name !== undefined) {
                properties.name = details.name;
            }
        } else if (
            _.intersection(details.types, [
                'point_of_interest',
                'restaurant',
                'meal_takeaway',
                'meal_delivery',
                'premise',
            ]).length
        ) {
            properties.type = 'business';
            if (details.name !== undefined) {
                properties.name = details.name;
            }
        } else {
            properties.type = 'residential';
            properties.name = '';
        }

        _.each(details.address_components, function(component) {
            _.each(map, function(types, field) {
                if (properties[field] === undefined) {
                    _.each(types, function(type) {
                        if (_.includes(component.types, type)) {
                            properties[field] = component.long_name;
                        }
                    });
                }
            });
        });

        // Some addresses that google return may not equivalents to
        // what we call suburbs, cities and regions. These fields are required,
        // so to avoid this, we backfill the region property with country,
        // the city property with region and the suburb property with city if
        // they are not provided.
        if (properties.region === undefined) {
            properties.region = properties.country;
        }

        if (properties.city === undefined) {
            properties.city = properties.region;
        }

        if (properties.suburb === undefined) {
            properties.suburb = properties.city;
        }

        // Extract street number from user's query:
        // Despite Google's service showing all possible street numbers for
        // addresses in the autocomplete, if Google doesn't believe the exact
        // street number exists, it won't be filled out in the form.
        if (
            prediction_description &&
            properties.street_name &&
            properties.street_number === undefined
        ) {
            var address_parts = prediction_description.split(' ');
            var search_street_number = address_parts[0];

            if (!/[A-Za-z]/.test(search_street_number)) {
                properties.street_number = search_street_number;
            }
        }

        // Extract unit from user's search query:
        // Google's place service does not provide information about street
        // address subunits. To ensure subunit (called 'specific_number' in the
        // address object) is not lost, we must extract the field from the
        // user's original address search query.
        if (
            prediction_description &&
            properties.specific_number === undefined
        ) {
            var address_parts = prediction_description.split(' ');
            var search_street_number = address_parts[0];
            if (search_street_number !== properties.street_number) {
                // Possible formats
                // 99999/123 Example St
                // 99999-123 Example St
                const delimiters = ['/', '-'];
                _.each(delimiters, function(delimiter) {
                    if (search_street_number.split(delimiter).length === 2) {
                        properties.specific_number = search_street_number.split(
                            delimiter
                        )[0];
                    }
                });

                // 99999 123 Example St
                if (address_parts[1] === properties.street_number) {
                    properties.specific_number = search_street_number;
                }

                // 123a Example St
                if (!properties.specific_number) {
                    const last_letter =
                        search_street_number[search_street_number.length - 1];
                    const first_part = search_street_number.substr(
                        0,
                        search_street_number.length - 1
                    );
                    if (
                        _.isNumber(first_part) &&
                        /[A-Fa-f]/.test(last_letter)
                    ) {
                        properties.specific_number = last_letter;
                    }
                }
            }
        }

        properties.geocode_search_string = prediction_description;

        return properties;
    },

    /**-------------------------------------------------------------------------
     */
    setFromGmapsPlaceDetail(detail, prediction_description) {
        return this.set(
            this.mapGmapsPlaceDetailFields(detail, prediction_description)
        );
    },

    /**-------------------------------------------------------------------------
     */
    getResidentialDisplayStreetNumberAndUnit() {
        let string = '';
        if (this.specific_number && this.street_number) {
            if (
                /[A-Za-z]/.test(this.specific_number) &&
                this.specific_number.length === 1
            ) {
                string += `${this.street_number + this.specific_number} `;
            } else if (this.specific_number.length <= 3) {
                string += `${this.specific_number}/${this.street_number} `;
            } else {
                string += `${this.specific_number}, ${this.street_number} `;
            }
        } else if (this.street_number) {
            string += `${this.street_number} `;
        }

        return string;
    },
});

/**-----------------------------------------------------------------------------
 */
Address.prototype.toString = function() {
    let string = '';

    switch (this.type) {
        case 'residential': {
            string += this.getResidentialDisplayStreetNumberAndUnit();
            break;
        }
        case 'business': {
            string += this.name ? `${this.name}, ` : '';
            string += this.specific_number
                ? `Level ${this.specific_number}, `
                : '';
            string += this.street_number ? `${this.street_number} ` : '';
            break;
        }
        case 'apartment': {
            string += this.name ? `${this.name}, ` : '';
            string += this.specific_number
                ? `Apt. ${this.specific_number}, `
                : '';
            string += this.street_number ? `${this.street_number} ` : '';
            break;
        }
        case 'hotel': {
            string += this.specific_number
                ? `Room ${this.specific_number}, `
                : '';
            string += this.name ? `${this.name}, ` : '';
            string += this.street_number ? `${this.street_number} ` : '';
            break;
        }
    }

    string += this.street_name ? `${this.street_name}, ` : '';
    string += this.suburb ? this.suburb : '';

    if (!this.street_name && this.post_code) {
        string += ` ${this.post_code}`;
    }

    if (this.comment) {
        string += ` (${this.comment})`;
    }

    return string;
};

/**-----------------------------------------------------------------------------
 * Settings that specify how addresses should behave
 */
var ADDRESS_SETTINGS = {
    custom: {},
    default: {
        properties: {
            type: {
                editable: false,
                visible: true,
                label: i18n.t('address:type'),
                placeholder: '',
                default_value: 'residential',
                validate(address) {
                    const prop = Address.PROPERTIES.TYPE;
                    const label = address.getPropertyLabel(prop);

                    return {
                        is_valid: !!address[prop],
                        message: `${label} ${i18n.t('address:required')}`,
                    };
                },
            },
            name: {
                editable: true,
                visible(address) {
                    return address.type !== 'residential';
                },
                label(address) {
                    return address.type == 'business'
                        ? i18n.t('address:business')
                        : i18n.t('address:building');
                },
                placeholder(address) {
                    return address.type == 'business'
                        ? i18n.t('address:business_placeholder')
                        : i18n.t('address:building_placeholder');
                },
                default_value: '',
                validate: null,
            },
            specific_number: {
                editable: true,
                visible: true,
                label: '',
                placeholder(address) {
                    switch (address.type) {
                        case 'business':
                            return i18n.t(
                                'address:specific_number_business_placeholder'
                            );
                        case 'residential':
                            return i18n.t(
                                'address:specific_number_residential_placeholder'
                            );
                        case 'hotel':
                            return i18n.t(
                                'address:specific_number_hotel_placeholder'
                            );
                        case 'apartment':
                            return i18n.t(
                                'address:specific_number_apartment_placeholder'
                            );
                        default:
                            return '';
                    }
                },
                default_value: '',
                validate: null,
            },
            street_number: {
                editable(address) {
                    // If lat/lon exists, the address was populated by the a geoservice
                    // therefore, this property should not be editable
                    return (
                        address.latitude === null && address.longitude === null
                    );
                },
                visible: true,
                label: i18n.t('address:street_number_label'),
                placeholder: i18n.t('address:street_number_placeholder'),
                default_value: '',
                validate(address) {
                    const prop = Address.PROPERTIES.STREET_NUMBER;
                    const label = address.getPropertyLabel(prop);
                    return {
                        is_valid: !!address[prop],
                        message: `${label} ${i18n.t('address:required')}`,
                    };
                },
            },
            street_name: {
                editable(address) {
                    // If lat/lon exists, the address was populated by the a geoservice
                    // therefore, this property should not be editable
                    return (
                        address.latitude === null && address.longitude === null
                    );
                },
                visible: true,
                label: i18n.t('address:street_label'),
                placeholder: i18n.t('address:street_placeholder'),
                default_value: '',
                validate(address) {
                    const prop = Address.PROPERTIES.STREET_NAME;
                    const label = address.getPropertyPlaceholder(prop);
                    return {
                        is_valid: !!address[prop],
                        message: `${label} ${i18n.t('address:required')}`,
                    };
                },
            },
            post_code: {
                editable(address) {
                    return (
                        address.latitude === null && address.longitude === null
                    );
                },
                visible: true,
                label: i18n.t('address:postal_code_label'),
                placeholder: i18n.t('address:postal_code_placeholder'),
                default_value: '',
                validate: null,
            },
            suburb: {
                editable(address) {
                    return (
                        address.latitude === null && address.longitude === null
                    );
                },
                visible: true,
                label: i18n.t('address:suburb_label'),
                placeholder: '',
                default_value: '',
                validate(address) {
                    return {
                        is_valid: address.suburb || address.suburb_id,
                        message: i18n.t('address:suburb_required'),
                    };
                },
            },
            city: {
                editable: false,
                visible: true,
                label: i18n.t('address:city_label'),
                placeholder: '',
                default_value: '',
                validate: null,
            },
            region: {
                editable: false,
                visible: true,
                label: i18n.t('address:region_label'),
                placeholder: '',
                default_value: '',
                validate: null,
            },
            latitude: {
                editable: false,
                visible: true,
                label: '',
                placeholder: '',
                default_value: '',
                validate: null,
            },
            longitude: {
                editable: false,
                visible: true,
                label: '',
                placeholder: '',
                default_value: '',
                validate: null,
            },
            comment: {
                editable: true,
                visible: true,
                label: i18n.t('address:comment_label'),
                placeholder: i18n.t('address:comment_placeholder'),
                default_value: '',
                validate: null,
            },
        },
        gmap_address_mapping: {
            name: ['premise'],
            specific_number: ['subpremise'],
            street_number: ['street_number'],
            street_name: ['route'],
            post_code: ['postal_code'],
            suburb: ['sublocality_level_1', 'locality', 'neighborhood'],
            city: [
                'administrative_area_level_2',
                'administrative_area_level_1',
                'locality',
            ],
            region: ['administrative_area_level_1'],
            country: ['country'],
        },
    },
    Japan: {
        properties: {
            street_name: {
                editable: true,
                visible: true,
            },
        },
        gmap_address_mapping: {
            name: ['premise'],
            street_number: ['street_number', 'sublocality_level_4'],
            street_name: ['route', 'sublocality_level_5'],
            post_code: ['postal_code'],
            suburb: ['sublocality_level_1', 'locality', 'neighborhood'],
            city: [
                'administrative_area_level_2',
                'administrative_area_level_1',
                'locality',
            ],
            region: ['administrative_area_level_1'],
            country: ['country'],
        },
    },
    Singapore: {
        properties: {
            street_name: {
                editable: true,
            },
        },
    },
    'New Zealand': {
        properties: {
            post_code: {
                label: i18n.t('address:nz_postal_code_label'),
                placeholder: i18n.t('address:nz_postal_code_placeholder'),
            },
            suburb: {
                label: i18n.t('address:nz_suburb_label'),
                placeholder: i18n.t('address:nz_suburb_label'),
            },
        },
    },
    Malaysia: {
        properties: {
            street_name: {
                editable: true,
            },
            street_number: {
                editable: true,
            },
        },
    },
    Philippines: {
        properties: {
            street_name: {
                editable: true,
            },
        },
    },
    Ireland: {
        properties: {
            street_number: {
                editable: true,
            },
            post_code: {
                label: i18n.t('address:ie_postal_code_label'),
                placeholder: i18n.t('address:ie_postal_code_placeholder'),
            },
        },
    },
    'United Kingdom': {
        properties: {
            post_code: {
                label: i18n.t('address:uk_postal_code_label'),
                placeholder: i18n.t('address:uk_postal_code_placeholder'),
            },
        },
    },
    'United States': {
        properties: {
            post_code: {
                label: i18n.t('address:us_postal_code_label'),
                placeholder: i18n.t('address:us_postal_code_placeholder'),
            },
        },
    },
    'Sri Lanka': {
        properties: {
            street_name: {
                editable: true,
            },
            street_number: {
                editable: true,
            },
        },
    }
};
